import { FC, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { Toolbar } from '@/components/ui/toolbar/ToolBar';
import { useTypedSelector, useTypedDispatch } from '@/store/hooks';
import { creatorActions } from '@/store/slices/creatorSlice';
import { Box, LoadingOverlay, CloseIcon } from '@mantine/core';
import {
	useResizeObserver,
	useFullscreenElement,
	useMergedRef,
} from '@mantine/hooks';
import { ToolbarButton } from '../partials/toolbarButton/ToolbarButton';
import { roundToHalf } from '@/utils/utilities';
import { RENDERER_CONFIG } from '@/configs/rendererConfig';
import styles from './ProductPreview.module.scss';

const renderer = ProjectRenderer.getInstance();

export const ProductPreview: FC = () => {
	const canvasRef = useRef<HTMLCanvasElement>(null!);

	const { renderer: rendererSlice } = useTypedSelector(
		(state) => state.creator
	);

	const dispatch = useTypedDispatch();

	const [resizeRef, rect] = useResizeObserver();
	const {
		ref: fullscreenRef,
		toggle: toggleFullscreen,
		fullscreen,
	} = useFullscreenElement();
	const mergedRef = useMergedRef(resizeRef, fullscreenRef);

	useLayoutEffect(() => {
		const canvas = canvasRef.current;

		renderer.init(canvas);
	}, []);

	useEffect(() => {
		renderer.handleResize();
	}, [rect.width, fullscreen]);

	const zoomEnabled = useMemo(
		() => ({
			in: rendererSlice.cameraDistance > RENDERER_CONFIG.CONTROLS.MIN_DISTANCE,
			out: rendererSlice.cameraDistance < RENDERER_CONFIG.CONTROLS.MAX_DISTANCE,
		}),
		[rendererSlice.cameraDistance]
	);

	const handleZoom = (zoomAction: 'in' | 'out') => () => {
		if (!renderer) return;

		let cameraDistance =
			rendererSlice.cameraDistance +
			(zoomAction === 'in'
				? -RENDERER_CONFIG.CAMERA_DISTANCE_TICK
				: RENDERER_CONFIG.CAMERA_DISTANCE_TICK);

		cameraDistance = Math.max(
			RENDERER_CONFIG.CONTROLS.MIN_DISTANCE,
			Math.min(cameraDistance, RENDERER_CONFIG.CONTROLS.MAX_DISTANCE)
		);

		cameraDistance = roundToHalf(cameraDistance);

		dispatch(
			creatorActions.updateRenderer({
				cameraDistance,
			})
		);

		renderer.handleCameraZoom(cameraDistance);
	};

	const handleCenterView = () => {
		renderer?.handleCameraCenter();
	};

	return (
		<Box component="main" className={styles.container}>
			<LoadingOverlay visible={rendererSlice.loading} />
			<Box ref={mergedRef} className={styles.fullscreenWrapper}>
				{fullscreen && (
					<ToolbarButton
						Icon={CloseIcon}
						tooltipProps={{ opened: false, label: '' }}
						onClick={toggleFullscreen}
						className={styles.closeFullScreenBtn}
					/>
				)}
				<Box ref={canvasRef} component="canvas"></Box>
			</Box>
			<Toolbar
				zoomEnabled={zoomEnabled}
				handleZoom={handleZoom}
				toggleFullscreen={toggleFullscreen}
				handleCenterView={handleCenterView}
			/>
		</Box>
	);
};
